import React, { useState } from 'react';
import { cn } from '@sglara/cn';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { Badge } from 'design-system/src/components/Badge/Badge';
import gradientCalendarIcon from 'design-system/src/assets/icons/gradientCalendar.png';
import { TrainingSessionOnboardingStatus } from '../../api';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../../constants';
import ScheduleTrainingModal from '../ScheduleTrainingModal/ScheduleTrainingModal';
import { createTrackingProps } from 'shared-utils/analytics';

export type ScheduleTrainingWidgetProps = {
  /**
   * Current training session onboarding status
   */
  trainingSessionStatus: TrainingSessionOnboardingStatus;
  /**
   * Layout variant for the booked state.
   * "page" shows a centered vertical layout (for the onboarding page).
   * "compact" shows a horizontal card layout (for the sidebar panel).
   */
  variant?: 'page' | 'compact';
  /**
   * Class name for additional styling
   */
  className?: string;
};

/**
 * Displays training scheduling UI based on the current training session status.
 * Shows a scheduling prompt when awaiting booking, a confirmation when booked,
 * or nothing when declined.
 */
export const ScheduleTrainingWidget = ({
  trainingSessionStatus,
  variant = 'page',
  className,
}: ScheduleTrainingWidgetProps) => {
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);
  const providerSupportEmailHref = `mailto:${PROVIDER_SUPPORT_CONTACT_INFO.email}`;

  if (trainingSessionStatus === 'awaiting_booking') {
    return (
      <>
        <div className={cn('flex w-full max-w-[480px] flex-col items-center gap-4', className)}>
          <div className="flex w-full flex-col items-center gap-2">
            <img src={gradientCalendarIcon} aria-hidden={true} className="size-16 shrink-0 object-contain" />
            <div className="flex w-full flex-col items-center gap-1">
              <Badge variant="primary" slim>
                Recommended
              </Badge>
              <Typography weight={600} renderedAs="h3">
                Schedule training for your team
              </Typography>
            </div>
            <Typography color="var(--grayscale-600)" className="text-center">
              Got questions? We'll walk you through account setup and show everything that'll be most useful for your
              workflow.
            </Typography>
          </div>
          <Button
            size="sm"
            onClick={() => setTrainingModalOpen(true)}
            {...createTrackingProps({ elementName: 'Schedule Training Widget: Schedule Training' })}
          >
            Schedule training
          </Button>
        </div>
        <ScheduleTrainingModal open={trainingModalOpen} onClose={() => setTrainingModalOpen(false)} />
      </>
    );
  }

  if (trainingSessionStatus === 'booked') {
    if (variant === 'compact') {
      return (
        <div
          className={cn(
            'flex min-w-0 items-center gap-4 rounded-lg border border-solid border-green-500 bg-green-50 px-6 py-4',
            className,
          )}
        >
          <img src={gradientCalendarIcon} aria-hidden className="size-16 shrink-0 object-contain" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Typography styledAs="bodyLargeDMSans" weight={600} color="var(--green-800)">
              You have a training scheduled!
            </Typography>
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
              Need to reschedule?{' '}
              <a
                href={providerSupportEmailHref}
                className="font-semibold text-purple-700 underline hover:text-purple-900"
                {...createTrackingProps({ elementName: 'Schedule Training Widget: Email Support (Compact)' })}
              >
                Email us
              </a>
            </Typography>
          </div>
        </div>
      );
    }

    return (
      <div className={cn('flex w-full max-w-[480px] flex-col items-center gap-4', className)}>
        <img src={gradientCalendarIcon} aria-hidden={true} className="size-16 shrink-0 object-contain" />
        <Typography weight={600} renderedAs="h3">
          You have a training scheduled!
        </Typography>
        <Typography color="var(--grayscale-600)" className="text-center">
          Need to reschedule?{' '}
          <a
            href={providerSupportEmailHref}
            className="text-purple-700 underline hover:text-purple-900"
            {...createTrackingProps({ elementName: 'Schedule Training Widget: Email Support (Page)' })}
          >
            Email us
          </a>
        </Typography>
      </div>
    );
  }

  return null;
};
